import { effect, Injectable, signal } from '@angular/core';
import { BrowserStorageService, WindowUtilitiesService } from '@grid-ui/common';
import { environment } from '@grid-ui/environment';
import { appendGoogleTagManagerScript, appendHotjarScript } from '@grid-ui/user-analytics';
import { CookieService } from 'ngx-cookie-service';
import * as R from 'ramda';
import { BehaviorSubject, Observable } from 'rxjs';
import { INITIAL_PRIVACY_SETTINGS_COOKIE, PRIVACY_SETTINGS_LOCAL_STORAGE_KEY } from '../constants';
import { CookiesBannerAcknowledgementState, PrivacySettings } from '../models';

@Injectable({
  providedIn: 'root',
})
export class PrivacySettingsStorageService {
  public get showCookiesBannerChanges(): Observable<boolean> {
    return this.showCookiesBannerSubject;
  }

  private showCookiesBannerSubject = new BehaviorSubject<boolean>(false);

  private privacySetting = signal<PrivacySettings>(null);

  protected readonly privacySettingsEffect = effect(() => {
    const settings = this.privacySetting();
    if (!settings) {
      return;
    }
    switch (settings.cookiesBannerState) {
      case CookiesBannerAcknowledgementState.ACCEPTED:
        appendHotjarScript(environment.appConfig.hotjarTrackingCode);
        appendGoogleTagManagerScript(environment.googleTagManagerId);
        break;
      case CookiesBannerAcknowledgementState.REJECTED:
        this.removeOptionalCookies();
        break;
      case CookiesBannerAcknowledgementState.NO_ACTION:
      default:
        this.showCookiesBannerSubject.next(true);
        break;
    }
  });

  constructor(
    private readonly windowUtilitiesService: WindowUtilitiesService,
    private readonly browserStorageService: BrowserStorageService,
    private readonly cookieService: CookieService,
  ) {
    this.privacySetting.set(this.getPrivacySettings());

    if (this.privacySetting() !== null) {
      this.showCookiesBannerSubject.next(
        this.privacySetting().cookiesBannerState === CookiesBannerAcknowledgementState.NO_ACTION ? true : false,
      );
    } else {
      this.setPrivacySettings(INITIAL_PRIVACY_SETTINGS_COOKIE);
    }
  }

  public modifyCookies(): void {
    const settings: PrivacySettings = this.privacySetting() !== null ? R.clone(this.privacySetting()) : INITIAL_PRIVACY_SETTINGS_COOKIE;
    settings.cookiesBannerState = CookiesBannerAcknowledgementState.NO_ACTION;
    this.setPrivacySettings(settings);
  }

  public acceptCookies(): void {
    const settings: PrivacySettings = this.privacySetting() !== null ? R.clone(this.privacySetting()) : INITIAL_PRIVACY_SETTINGS_COOKIE;
    settings.performanceEnabled = true;
    settings.cookiesBannerState = CookiesBannerAcknowledgementState.ACCEPTED;
    this.setPrivacySettings(settings);
  }

  public rejectCookies(): void {
    const settings: PrivacySettings = this.privacySetting() !== null ? R.clone(this.privacySetting()) : INITIAL_PRIVACY_SETTINGS_COOKIE;
    settings.performanceEnabled = false;
    settings.cookiesBannerState = CookiesBannerAcknowledgementState.REJECTED;
    this.setPrivacySettings(settings);
    this.windowUtilitiesService.reloadApp();
  }

  public getPrivacySettings(): PrivacySettings | null {
    if (!this.windowUtilitiesService.supportsLocalStorage()) {
      return null;
    }
    const storedSettings = this.browserStorageService.getLocal<PrivacySettings>(PRIVACY_SETTINGS_LOCAL_STORAGE_KEY);
    return storedSettings ? { ...INITIAL_PRIVACY_SETTINGS_COOKIE, ...storedSettings } : null;
  }

  public setPrivacySettings(settings: PrivacySettings): void {
    const normalizedSettings = { ...INITIAL_PRIVACY_SETTINGS_COOKIE, ...settings };
    this.privacySetting.set(normalizedSettings);
    if (this.windowUtilitiesService.supportsLocalStorage()) {
      this.browserStorageService.setLocal(PRIVACY_SETTINGS_LOCAL_STORAGE_KEY, normalizedSettings);
    }
    this.showCookiesBannerSubject.next(normalizedSettings.cookiesBannerState === CookiesBannerAcknowledgementState.NO_ACTION);
  }

  public isPerformanceEnabled(): boolean {
    const settings = this.privacySetting();
    return settings ? settings.performanceEnabled : INITIAL_PRIVACY_SETTINGS_COOKIE.performanceEnabled;
  }

  public confirmPerformancePreference(enabled: boolean): void {
    const settings: PrivacySettings = this.privacySetting() !== null ? R.clone(this.privacySetting()) : INITIAL_PRIVACY_SETTINGS_COOKIE;
    settings.performanceEnabled = enabled;
    settings.cookiesBannerState = enabled ? CookiesBannerAcknowledgementState.ACCEPTED : CookiesBannerAcknowledgementState.REJECTED;
    this.setPrivacySettings(settings);
    this.windowUtilitiesService.reloadApp();
  }

  private removeOptionalCookies(): void {
    const allCookies: string[] = this.cookieService.getAll() ? Object.keys(this.cookieService.getAll()) : [];
    const optionalCookies: string[] = allCookies.filter(
      (cookieName: string) => cookieName.startsWith('_g') || cookieName.startsWith('_hj'),
    );
    optionalCookies.forEach((cookieName: string) => {
      this.cookieService.delete(cookieName, '/');
    });
  }
}
